'use client';

import {RecaptchaScript} from '@gfiber/third-parties/google';
import {clsx} from '@gfiber/typesmith';
import {zodResolver} from '@hookform/resolvers/zod';
import {Radio} from '@pixel-party/Radio/v2';
import {useParams} from 'next/navigation';
import type React from 'react';
import {useId} from 'react';
import {Controller, type SubmitHandler, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {useSubmit} from '@/app/forms/_hooks/useSubmit';
import {REGISTRY} from '@/app/forms/_registry/client';
import * as constants from '@/app/forms/constants';
import {
  RELATIONSHIPS_MAP,
  REQUEST_TYPES_MAP,
  RESPONSE_TYPES_MAP,
} from '@/app/forms/i18n';
import {ApiError, type InferFormValues} from '@/app/forms/types';
import {FormError, TextField} from '@/components';
import {isRequired, maxLength} from '@/lib/utils/common';
import {
  getRootError,
  getSchemaShape,
  getSfdcLanguage,
  handleError,
  onSubmitError,
  tSafe,
} from '../../utils';
import {Consent} from '../Consent/Consent';
import {Description, FormDescription} from '../Description/Description';
import {SubmitButton} from '../SubmitButton/SubmitButton';
import {SuccessMessage} from '../SuccessMessage/SuccessMessage';

function DataRequestForm(): React.JSX.Element {
  const {t} = useTranslation();

  const config = REGISTRY.DataRequestForm;
  type DataRequestFormData = InferFormValues<typeof config>;
  const params = useParams();

  const {
    control,
    formState: {errors, isSubmitted, isValid},
    handleSubmit,
    setError,
  } = useForm<DataRequestFormData>({
    resolver: zodResolver(config.schema),
    mode: 'onChange',
    defaultValues: {
      accountId: '',
      email: '',
      firstName: '',
      lastName: '',
      street: '',
      apt: '',
      city: '',
      zipCode: '',
      state: '',
      typeOfRequest: '',
      typeOfResponse: '',
      requesterRelationship: '',
      formType: 'Data_Request',
      language: getSfdcLanguage(params?.locale),
    },
  });
  const fields = getSchemaShape(config.schema);
  const {mutate, isPending, isSuccess} = useSubmit<DataRequestFormData>(
    config.id,
  );
  const id = useId();

  const onSubmit: SubmitHandler<DataRequestFormData> = async data => {
    const recaptchaToken = await window.grecaptcha?.enterprise.execute(
      constants.RECAPTCHA_SITE_KEY,
      {action: config.recaptchaAction || 'submit_form'},
    );

    if (!recaptchaToken) {
      handleError(
        new ApiError(t('errors.recaptcha'), {
          code: 'RECAPTCHA_FAILED',
          message: 'reCAPTCHA token could not be obtained',
        }),
        setError,
        fields,
      );
      return;
    }

    mutate(
      {data, recaptchaToken},
      {
        onError: error => {
          handleError(error, setError, fields);
        },
      },
    );
  };

  return (
    <>
      <h1 className="title">{t(config.title)}</h1>
      {isSuccess ? (
        <SuccessMessage />
      ) : (
        <form
          className="form"
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
          noValidate
        >
          <fieldset disabled={isPending}>
            <FormDescription>
              <Description>
                <Trans i18nKey="forms.dataRequest.description1">
                  <a
                    href={constants.PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Notice
                  </a>
                </Trans>
              </Description>
              <Description>
                <Trans i18nKey="forms.dataRequest.description2">
                  <span className="bolder">
                    Are you an existing GFiber customer?
                  </span>
                </Trans>
              </Description>
            </FormDescription>
            <div className="field">
              <Controller
                control={control}
                name="accountId"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.dataRequest.accountId')}
                    id={`accountId-${id}`}
                    required={isRequired(fields.accountId)}
                    errorText={tSafe(errors.accountId?.message, t)}
                    maxLength={maxLength(fields.accountId)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="email"
                render={({field}) => (
                  <TextField
                    {...field}
                    type="email"
                    label={t('forms.dataRequest.email')}
                    id={`email-${id}`}
                    required={isRequired(fields.email)}
                    errorText={tSafe(errors.email?.message, t)}
                    maxLength={maxLength(fields.email)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="firstName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.dataRequest.firstName')}
                    id={`firstName-${id}`}
                    required={isRequired(fields.firstName)}
                    errorText={tSafe(errors.firstName?.message, t)}
                    maxLength={maxLength(fields.firstName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="lastName"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.dataRequest.lastName')}
                    id={`lastName-${id}`}
                    required={isRequired(fields.lastName)}
                    errorText={tSafe(errors.lastName?.message, t)}
                    maxLength={maxLength(fields.lastName)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="street"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.dataRequest.street')}
                    id={`street-${id}`}
                    required={isRequired(fields.street)}
                    errorText={tSafe(errors.street?.message, t)}
                    maxLength={maxLength(fields.street)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="apt"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.dataRequest.apt')}
                    id={`apt-${id}`}
                    required={isRequired(fields.apt)}
                    errorText={tSafe(errors.apt?.message, t)}
                    maxLength={maxLength(fields.apt)}
                  />
                )}
              />
            </div>
            <div className="field">
              <Controller
                control={control}
                name="city"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.dataRequest.city')}
                    id={`city-${id}`}
                    required={isRequired(fields.city)}
                    errorText={tSafe(errors.city?.message, t)}
                    maxLength={maxLength(fields.city)}
                  />
                )}
              />
            </div>
            <fieldset className="field">
              <legend className="legend">
                {t('forms.dataRequest.state')}:{' '}
                {isRequired(fields.state) && '*'}
              </legend>
              <Controller
                name="state"
                control={control}
                render={({field: {onChange, value, ref}}) => (
                  <div
                    ref={ref}
                    className="radioGroup"
                  >
                    {constants.DATA_REQUEST_STATES.map(state => (
                      <Radio
                        key={state.code}
                        id={`state-${state.code}`}
                        name="dataRequestState"
                        value={state.code}
                        label={state.name}
                        checked={value === state.code}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              />
              <FormError message={tSafe(errors.state?.message, t)} />
            </fieldset>
            <div className="field">
              <Controller
                control={control}
                name="zipCode"
                render={({field}) => (
                  <TextField
                    {...field}
                    label={t('forms.dataRequest.zipCode')}
                    id={`zipCode-${id}`}
                    required={isRequired(fields.zipCode)}
                    errorText={tSafe(errors.zipCode?.message, t)}
                    maxLength={maxLength(fields.zipCode)}
                  />
                )}
              />
            </div>
            <fieldset className="field">
              <legend className="legend">
                {t('forms.dataRequest.typeOfRequest')}{' '}
                {isRequired(fields.typeOfRequest) && '*'}
              </legend>
              <Controller
                name="typeOfRequest"
                control={control}
                render={({field: {onChange, value, ref}}) => (
                  <div
                    ref={ref}
                    className="radioGroup"
                  >
                    {REQUEST_TYPES_MAP.map(item => (
                      <Radio
                        key={item.id}
                        id={`request-type-${item.id}`}
                        name="dataRequestType"
                        value={item.value}
                        label={t(item.i18nKey, {defaultValue: item.id})}
                        checked={value === item.value}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              />
              <FormError message={tSafe(errors.typeOfRequest?.message, t)} />
            </fieldset>
            <fieldset className="field">
              <legend className="legend">
                {t('forms.dataRequest.typeOfResponse')}{' '}
                {isRequired(fields.typeOfResponse) && '*'}
              </legend>
              <Controller
                name="typeOfResponse"
                control={control}
                render={({field: {onChange, value, ref}}) => (
                  <div
                    ref={ref}
                    className="radioGroup"
                  >
                    {RESPONSE_TYPES_MAP.map(item => (
                      <Radio
                        key={item.id}
                        id={`response-type-${item.id}`}
                        name="dataRequestResponseType"
                        value={item.value}
                        label={t(item.i18nKey, {defaultValue: item.id})}
                        checked={value === item.value}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              />
              <FormError message={tSafe(errors.typeOfResponse?.message, t)} />
            </fieldset>
            <fieldset className="field">
              <legend className={clsx('legend', 'bolder')}>
                {t('forms.dataRequest.certify')}{' '}
                {isRequired(fields.requesterRelationship) && '*'}
              </legend>
              <Controller
                name="requesterRelationship"
                control={control}
                render={({field: {onChange, value, ref}}) => (
                  <div
                    ref={ref}
                    className="radioGroup"
                  >
                    {RELATIONSHIPS_MAP.map(item => (
                      <Radio
                        key={item.id}
                        id={`relationship-${item.id}`}
                        name="requesterRelationship"
                        value={item.value}
                        label={t(item.i18nKey, {defaultValue: item.id})}
                        checked={value === item.value}
                        onChange={onChange}
                      />
                    ))}
                  </div>
                )}
              />
              <FormError
                message={tSafe(errors.requesterRelationship?.message, t)}
              />
            </fieldset>
            <div className="field">
              <Description className="bolder">
                {t('forms.dataRequest.additionalInfo.title')}
              </Description>
              <Description>
                {t('forms.dataRequest.additionalInfo.p1')}
              </Description>
              <Description>
                {t('forms.dataRequest.additionalInfo.p2')}
              </Description>
            </div>
            <div className="field">
              <Description className="bolder">
                {t('forms.dataRequest.acknowledgement.title')}
              </Description>
              <Description>
                {t('forms.dataRequest.acknowledgement.text')}
              </Description>
            </div>
            <SubmitButton
              isDisabled={isPending}
              isPending={isPending}
              rootError={getRootError(errors, isSubmitted, isValid, t)}
            />
            <Consent />
          </fieldset>
          {constants.RECAPTCHA_SITE_KEY && (
            <RecaptchaScript siteKey={constants.RECAPTCHA_SITE_KEY} />
          )}
        </form>
      )}
    </>
  );
}

export {DataRequestForm};
